import { Fragment, type ReactNode } from "react";
import type { Repeat, User } from "./api";

/** Message text, Slack-style: *bold*, _italic_, ~strike~, `code`, ```code blocks```, > quotes, lists, links and @mentions. */
export function Rich({ text, names = [], me }: { text: string; names?: string[]; me?: string }) {
  const parts = text.split(/```/);
  return <>{parts.map((p, i) => i % 2 === 1
    ? <pre key={i} className="cb">{p.replace(/^\n/, "").replace(/\n$/, "")}</pre>
    : <Blocks key={i} text={p} names={names} me={me} />)}</>;
}

function Blocks({ text, names, me }: { text: string; names: string[]; me?: string }) {
  const lines = text.replace(/^\n|\n$/g, "").split("\n");
  const out: ReactNode[] = [];
  let i = 0, k = 0;
  const run = (test: (l: string) => boolean) => { const start = i; while (i < lines.length && test(lines[i])) i++; return lines.slice(start, i); };
  while (i < lines.length) {
    const l = lines[i];
    if (/^&gt; |^> /.test(l)) out.push(<blockquote key={k++}>{run((x) => /^> /.test(x)).map((x, j) => <Fragment key={j}>{j > 0 && <br />}{inline(x.slice(2), names, me)}</Fragment>)}</blockquote>);
    else if (/^\s*[-*•] /.test(l)) out.push(<ul key={k++}>{run((x) => /^\s*[-*•] /.test(x)).map((x, j) => <li key={j}>{inline(x.replace(/^\s*[-*•] /, ""), names, me)}</li>)}</ul>);
    else if (/^\s*\d+[.)] /.test(l)) out.push(<ol key={k++}>{run((x) => /^\s*\d+[.)] /.test(x)).map((x, j) => <li key={j}>{inline(x.replace(/^\s*\d+[.)] /, ""), names, me)}</li>)}</ol>);
    else { const para = run((x) => !/^> |^\s*[-*•] |^\s*\d+[.)] /.test(x)); out.push(<Fragment key={k++}>{para.map((x, j) => <Fragment key={j}>{j > 0 && <br />}{inline(x, names, me)}</Fragment>)}</Fragment>); if (i < lines.length) out.push(<br key={k++} />); }
  }
  return <>{out}</>;
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
function inline(line: string, names: string[], me?: string): ReactNode[] {
  const who = names.length ? `|(?<![\\p{L}\\p{N}_@])@(${[...names].sort((a, b) => b.length - a.length).map(esc).join("|")})(?![\\p{L}\\p{N}_])` : "";
  const re = new RegExp(`(https?:\\/\\/[^\\s<]+[^\\s<.,;:!?)\\]'"])|\`([^\`]+)\`|\\*\\*([^*\\n]+)\\*\\*|(?<![\\p{L}\\p{N}])\\*([^*\\n]+)\\*(?![\\p{L}\\p{N}])|(?<![\\p{L}\\p{N}])_([^_\\n]+)_(?![\\p{L}\\p{N}])|~([^~\\n]+)~${who}`, "giu");
  const out: ReactNode[] = [];
  let last = 0, m: RegExpExecArray | null, k = 0;
  while ((m = re.exec(line))) {
    if (m.index > last) out.push(line.slice(last, m.index));
    if (m[1]) out.push(<a key={k++} href={m[1]} target="_blank" rel="noopener noreferrer">{m[1]}</a>);
    else if (m[2]) out.push(<code key={k++}>{m[2]}</code>);
    else if (m[3] || m[4]) out.push(<b key={k++}>{m[3] || m[4]}</b>);
    else if (m[5]) out.push(<i key={k++}>{m[5]}</i>);
    else if (m[6]) out.push(<s key={k++}>{m[6]}</s>);
    else if (m[7]) out.push(<span key={k++} className={`mention${me && m[7].toLowerCase() === me.toLowerCase() ? " me" : ""}`}>@{m[7]}</span>);
    last = re.lastIndex;
  }
  if (last < line.length) out.push(line.slice(last));
  return out;
}

export const plain = (s: string) => s.replace(/\s+/g, " ").trim();

// ---------- dates ----------

export const localToday = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseDay = (s: string) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const addDays = (s: string, n: number) => { const d = parseDay(s); d.setDate(d.getDate() + n); return localToday(d); };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function dueLabel(due: string | null) {
  if (!due) return "";
  const t = localToday();
  if (due === t) return "today";
  if (due === addDays(t, 1)) return "tomorrow";
  if (due === addDays(t, -1)) return "yesterday";
  const diff = Math.round((parseDay(due).getTime() - parseDay(t).getTime()) / 86400000);
  if (diff > 1 && diff < 7) return DAYS[parseDay(due).getDay()];
  const d = parseDay(due);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${d.getFullYear() !== new Date().getFullYear() ? ` ${d.getFullYear()}` : ""}`;
}
export const isLate = (t: { due: string | null; status: string }) => !!t.due && t.status !== "done" && t.due < localToday();

export function timeLabel(iso: string) {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}
export function dayLabel(iso: string) {
  const d = localToday(new Date(iso)), t = localToday();
  if (d === t) return "Today";
  if (d === addDays(t, -1)) return "Yesterday";
  const date = new Date(iso);
  return date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", ...(date.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}) });
}

export const REPEAT_LABEL: Record<Repeat, string> = { daily: "every day", weekdays: "every weekday", weekly: "every week", monthly: "every month" };

// ---------- "Hannah, check the Brookside report by Friday" ----------

export interface Parsed { title: string; ownerId: number | null; due: string | null; repeat: Repeat | null }

export function parseTask(text: string, people: User[], fallbackOwner: number | null): Parsed {
  let s = text.trim(), ownerId: number | null = null, due: string | null = null, repeat: Repeat | null = null;
  const lead = people.find((u) => new RegExp(`^${u.name}\\b[,:]?\\s*`, "i").test(s));
  if (lead) { ownerId = lead.id; s = s.replace(new RegExp(`^${lead.name}\\b[,:]?\\s*`, "i"), ""); }
  const rep: [RegExp, Repeat][] = [[/\s*,?\s*every\s+weekday\b/i, "weekdays"], [/\s*,?\s*(every\s+day|daily)\b/i, "daily"], [/\s*,?\s*(every\s+week|weekly)\b/i, "weekly"], [/\s*,?\s*(every\s+month|monthly)\b/i, "monthly"]];
  for (const [re, r] of rep) if (re.test(s)) { repeat = r; s = s.replace(re, ""); break; }
  const t = localToday();
  const m = s.match(/\s*,?\s*\b(?:by|due|on)?\s*(today|tomorrow|mon(?:day)?|tue(?:sday)?|wed(?:nesday)?|thu(?:rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?|\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*)\s*$/i);
  if (m) {
    const w = m[1].toLowerCase();
    if (w === "today") due = t;
    else if (w === "tomorrow") due = addDays(t, 1);
    else if (/^\d/.test(w)) {
      const [d, mon] = w.split(/\s+/);
      const mi = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"].indexOf(mon.slice(0, 3));
      const now = parseDay(t);
      let date = new Date(now.getFullYear(), mi, Number(d));
      if (date < now) date = new Date(now.getFullYear() + 1, mi, Number(d));
      due = localToday(date);
    } else {
      const target = DAYS.findIndex((x) => w.startsWith(x.toLowerCase()));
      const now = parseDay(t).getDay();
      due = addDays(t, ((target - now + 7) % 7) || 7);
    }
    s = s.slice(0, m.index);
  }
  s = s.replace(/[\s,.]+$/, "");
  return { title: s ? s[0].toUpperCase() + s.slice(1) : "", ownerId: ownerId ?? fallbackOwner, due, repeat };
}
